import { FeiEngine, FeiVO, FreedataCall, VisionStepPerimeterCommand } from '@wizeoo/akm-common-lib';
import { BATCH_KIND } from '@wizeoo/akm-common-lib';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { WizMathUtils, WIZ_EVENT_TYPE,  
  CONFIRMATION_MODE, CONFIRMATION_RESULT } from '@wizeoo/akm-core-lib';
import {
  BATCH_FILE_FORMAT,
  ConfirmationResultObj,
  FilterComponent,
  WizButton,
  WizDatasource,
  WizDropdown,
  WizGroupCommand,
  WizImage,
  WizLabel,
  WizPopUp,
  WizTextInput,
  WizTranslationBasic,
  WizValidationContainerDirective,
  WizVgroup,
  WizEventManager, WizEvent
} from '@wizeoo/akm-component-lib';
import { FormulaEngineInstance, Baseline, VisionStepPerimeter, VisionStep } from '@wizeoo/akm-message-lib';
import { VisionManageLinesCmd } from '@wizeoo/akm-common-lib';
import { VisionStatsCmd } from '@wizeoo/akm-common-lib';
import { MultiDisplay, MultiDisplayConfig, UserContextService } from '@wizeoo/akm-common-lib';
import { AkmBDD } from '@wizeoo/akm-worker-lib';
import { PermissionAccess } from '@wizeoo/akm-common-lib';
import { ViewData } from '@wizeoo/akm-common-lib';
import { VisionStepVO } from '@wizeoo/akm-common-lib';
import { VisionStepPerimeterVO } from '@wizeoo/akm-common-lib';
import { PermissionVisibility, StaticFeatureEnum } from '@wizeoo/akm-common-lib';
import { BaseView } from '@wizeoo/akm-common-lib';
import { Model } from '../../model/model';
import { ChronocodeCmd } from '@wizeoo/akm-common-lib';
import { ALK_EVENT_TYPE, VISION_PROPERTY, WIDGET_ACTION_ORIGIN, WIDGET_STATE } from '@wizeoo/akm-common-lib';
import { AlkEvent } from '@wizeoo/akm-common-lib';
import { VisionThumbRenderer } from './renderer/vision-thumb.renderer';
import { BaselineEngine, VisionVO, WizConstants } from '@wizeoo/akm-compute-lib';
import { ToolPivot } from '@wizeoo/akm-common-lib';

/**
 * Vue d'administation des profils utilisateurs
 * Author : sva
 */
@Component({
  selector: 'vision-generate-view',
  templateUrl: './vision-generate.view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VisionGenerateView extends BaseView {
  @ViewChild('multiDisp', { read: MultiDisplay }) multiDisp: MultiDisplay<VisionVO>;
  @ViewChild('vgDetailZone', { read: WizVgroup, static: false }) vgDetailZone: WizVgroup;
  @ViewChild('gdSteps', { read: MultiDisplay }) gdSteps: MultiDisplay<VisionStepVO>;

  /** rendu de la vision */
  @ViewChild('lblTitle', { read: WizLabel }) lblTitle: WizLabel;
  @ViewChild('uploadImg', { read: WizImage }) uploadImg: WizImage;

  @ViewChild('btnGenerate', { read: WizButton }) btnGenerate: WizButton; // btn génération lignes
  @ViewChild('btnGenerateDelta', { read: WizButton }) btnGenerateDelta: WizButton; // btn génération lignes (delta, pas full)
  @ViewChild('btnDelete', { read: WizButton }) btnDelete: WizButton; // btn suppression lignes
  @ViewChild('btnReset', { read: WizButton }) btnReset: WizButton; // btn suppression values
  @ViewChild('btnSample', { read: WizButton }) btnSample: WizButton; // btn génération sample
  @ViewChild('btnclearCache', { read: WizButton }) btnclearCache: WizButton; // reset du cache local

  @ViewChild('vgEdition', { read: WizVgroup, static: false }) vgEdition: WizVgroup; // zone détail
  @ViewChild(WizValidationContainerDirective, { read: WizValidationContainerDirective, static: false }) private vgEditionValidator: WizValidationContainerDirective;


  public visionDS: WizDatasource<VisionVO> = new WizDatasource<VisionVO>([])
    .setSortFields([{ property: 'code', way: 'asc' }])
    .setFilter(this.visionFilterFunc.bind(this))
    .build();

  /** Data source des objets de construction */
  public stepDS: WizDatasource<VisionStepVO> = new WizDatasource<VisionStepVO>([])
    .setSortFields([{ property: 'level', way: 'asc' }])
    .setSelectionMode('multiple')
    .build();

  /** stepPerimeters */
  public stepPerimetersDS: WizDatasource<VisionStepPerimeterVO> = new WizDatasource<VisionStepPerimeterVO>([])
    .setSortFields([{ property: 'levelAndLabel', way: 'asc' }])
    .setSelectionMode('multiple')
    .build();

  /** DS de la drop de statuts */
  // private visionStateDS: WizDatasource<FakeVO> = new WizDatasource<FakeVO>(ViewData.getInstance().visionStateList)
  //   .setSortFields([{ property: 'order', way: 'asc' }])
  //   .build();

  /** steps de la vision en cours pour alimentation de la combo de simulation */
  public stepChooserDS: WizDatasource<VisionStepVO> = new WizDatasource<VisionStepVO>([])
    .setItemCodeField('code')
    .setSortFields([{ property: 'level', way: 'asc', numeric: true }])
    .setSelectionMode('single')
    .build();

  /**
   * filtre la liste des visions pour masquer les visions de conf (BU)
   * @param item visionVO
   * @returns boolean, true si l'element correspond au filtre, false sinon
   */
  private visionFilterFunc(item: VisionVO): boolean {
    if (Model.getInstance().visionEngine.getVisionProperties(item, VISION_PROPERTY.ISCONFIGAPP)) {
      return false;
    }
    return true;
  }

  /**
   * Constructeur
   * @param elementRef elementRef pour pose d'écouteurs
   */
  constructor(public elementRef: ElementRef, protected cdref: ChangeDetectorRef) {
    super(elementRef);
  }

  private get _context(): UserContextService {
    return Model.getInstance().userContextService;
  }

  public currentVision: VisionVO = null; // vision en cours, sert aussi de flag pour afficher le détail / le cartouche

  /* #############################################################
                        CYCLE DE VIE WizView
     ############################################################# */

  /**
   * Pretraitement: chargement des données nécessaires à la vue
   * @return WizGroupCommand
   */
  public wizBeforeInit(): WizGroupCommand {
    super.wizBeforeInit();
    const commands = [];

    // disponibilité des chronocodes pour labels
    if (!Model.getInstance().chronocodeEngine.getChronocodesMap()) {
      commands.push(new ChronocodeCmd(Model.getInstance()).find());
    }

    // disponibilité des freedatas pour labels
    if (!Model.getInstance().freedataEngine.getFreedataMap()) {
      commands.push(new FreedataCall(Model.getInstance()));
    }

    // une vision en cours, mais ses vsp pas chargés, chargement direct
    // if (Model.getInstance().currentVision && !Model.getInstance().visionEngine.getStepPerimeterList(Model.getInstance().currentVision)) {
    //   commands.push(new VisionStepPerimeterCommand().find(Model.getInstance().currentVision));
    // }

    if (commands.length > 0) {
      return new WizGroupCommand().configure(commands);
    } else {
      return null;
    }
  }

  // private get viewPermissions(): ViewPermission[] {
  //   return [
  //     {
  //       feature: StaticFeatureEnum.VISION_GENERATE,
  //       permissions: [
  //         {
  //           component: this.vgDetailZone,
  //           accesses: [PermissionAccess.UPDATE, PermissionAccess.CREATE],
  //           visibility: PermissionVisibility.DISABLED,
  //         }
  //       ]
  //     }
  //   ];
  // }

  /**
   * Synchronisation des données
   */
  public wizSynchronizeData(): void {

    this.visionDS.setSource([...Model.getInstance().visionEngine.visionDS.getSource()]);


    // construction des filtres du grid
    this.buildFilter();
    this.feed();
  }

  /**
   * Mise en place des écouteurs
   */
  public wizAddListener(): void {
    super.wizAddListener();
    WizEventManager.addListener(this, this.vgEdition, WIZ_EVENT_TYPE.MODEL_UPDATE, this.updateModel); // écouteur global du change pour modelUpdate
    WizEventManager.addListener(this, this.vspKeyMatrix, ALK_EVENT_TYPE.VSP_SELECT_CHANGED, this.updateVspSelect); // écouteur sur grid vsp
    WizEventManager.addListener(this, this.btnSaveStep, WIZ_EVENT_TYPE.CLICK, this.doMerge);
    // revert vsp (btCancel)
    WizEventManager.addListener(this, this.btnCancelStep, WIZ_EVENT_TYPE.CLICK, this.doRevert);
  }

  /**
   * Suppression des écouteurs
   */
  public wizRemoveListener(): void {
    super.wizRemoveListener();
    WizEventManager.removeListener(this, this.vgEdition, WIZ_EVENT_TYPE.MODEL_UPDATE); // écouteur global du change pour modelUpdate
    WizEventManager.removeListener(this, this.vspKeyMatrix, ALK_EVENT_TYPE.VSP_SELECT_CHANGED); // écouteur sur grid vsp
    WizEventManager.removeListener(this, this.btnSaveStep, WIZ_EVENT_TYPE.CLICK);
    WizEventManager.removeListener(this, this.btnCancelStep, WIZ_EVENT_TYPE.CLICK);
  }

  /** mémorisation du filtre */
  public wizOnDestroy(): void {
    // this._context.saveLocalFilter(this.mainFilter);
    super.wizOnDestroy()
  }

  /* ##############################################################################
                    Panneau gauche : Liste, sélection, filtres
     ############################################################################## */

  /**
   * configuration de l'affichage en multi liste
   */
  public multiDisplayConfig: MultiDisplayConfig<VisionVO> = {
    model: Model.getInstance(),
    datasource: this.visionDS,
    defaultMode: 'grid',
    onSelectionChanging: this.onWizEventSelectionChange.bind(this),
    grid: {
      enabled: true,
      columns: [
        {
          headerName: ' ',
          field: 'code',
          width: 40,
          sortable: false,
          cellRenderer: 'visionThumbRenderer'
        },
        {
          headerName: '$.common.code.$',
          field: 'code',
          tooltipField: 'code',
          width: 100,
          sortable: true,
        },
        {
          headerName: '$.common.label.$',
          field: 'trsLabel',
          tooltipField: 'trsLabel',
          flex: 1,
          sortable: false,
        },
      ],
      renderers: {
        visionThumbRenderer: VisionThumbRenderer
      },
    },
    filter: {
      enabled: true,
      initFilter: this.initFilter.bind(this),
    },
  };

  private mainFilter: FilterComponent;

  private initFilter(pFilter: FilterComponent): FilterComponent {
    this.mainFilter = pFilter;
    return this.mainFilter;
  }

  /**
   * configuration de la liste de steps
   */
  public stepConfig: MultiDisplayConfig<VisionStepVO> = {
    model: Model.getInstance(),
    datasource: this.stepDS,
    defaultMode: 'grid',
    grid: {
      rowSelection: 'multiple',
      enabled: true,
      columns: [
        { headerName: '$.common.label.$', field: 'levelAndLabel', flex: 1 },
        { headerName: '$.visionstep.lines.$', field: 'nbLines', width: 100 },
        { headerName: '$.visionstep.values.$', field: 'nbValues', width: 120 },
      ],
      suppressRowClickSelection: true,
    },
  };

  /**
   * configuration des stepPerimeters
   */
  public perimetersConfig: MultiDisplayConfig<VisionStepPerimeterVO> = {
    model: Model.getInstance(),
    datasource: this.stepPerimetersDS,
    onSelectionChanging: this.selectVsp.bind(this),
    defaultMode: 'grid',
    grid: {
      enabled: true,
      rowSelection: 'single',
      columns: [
        { headerName: '$.common.label.$', field: 'levelAndLabel', flex: 1 }
      ]
    }
  };

  /**
   * construction de la zone de filtre des visions
   * @param event : Evenement de changement dans le text input de filtre
   */
  private buildFilter(event: WizEvent = null): void {
    this.mainFilter.configure(['code', 'trsLabel'], null, null);
    // le contextEngine prend le relais pour alimenter les filtres par défaut
    this._context.feedFilterContext(this.mainFilter);
  }

  /**
   * Callback de selection dans le grid
   * @param pEvent WizEvent
   */
  private onWizEventSelectionChange(e: WizEvent): void {
    const vo: VisionVO = e.value;
    // this.currentVision = e.value ? e.value : null; 
    if (!this.visionDS.isSelected(vo)) {
      // pas encore sélectionné
      this.currentVision = vo;
    } else {
      // déjà sélectionné, déselection
      this.currentVision = null;
    }

    if (this.currentVision) {
      const commands = [];
      commands.push(new VisionStatsCmd(Model.getInstance()).do(this.currentVision, null));// chargement des stats

      // vsp pas encore chargés
      if (this.currentVision && !Model.getInstance().visionEngine.getStepPerimeterList(this.currentVision)) {
        commands.push(new VisionStepPerimeterCommand(Model.getInstance()).find(this.currentVision));
      }

      new WizGroupCommand().configure(commands, this.feed.bind(this)).execute();
    } else {
      this.feed();
    }
  }

  /* #########################################
                    Detail
     ######################################### */

  public get stepExist(): boolean {
    return this.clonePerimeter?.exist;
  }

  public isDetailVisible() {
    const result =
      !!this.currentVision &&
      Model.getInstance().permissionEngine.hasAnyPermission({
        feature: StaticFeatureEnum.VISION_GENERATE,
        accesses: [PermissionAccess.READ, PermissionAccess.CREATE, PermissionAccess.UPDATE],
        visibility: PermissionVisibility.DISABLED,
      });
    return result;
  }

  /**
   * choix d'une vision
   */
  public feed(): void {

    // neutralisation map de fei
    this.loadedFeiMap = null;
    this.vspKeyMatrix.context = null;

    // neutralisation de la partie FDV
    this.currentPerimeter = null;
    this.clonePerimeter = null;
    this.feedVsp();



    if (!this.currentVision) {
      // uniquement si la vision est alimentée
      return;
    }

    this.lblTitle.wizLabel = Model.getInstance().visionEngine.getVisionLabel(this.currentVision) + ' (' + this.currentVision.code + ')';
    this.uploadImg.sourceObservable = Model.getInstance().visionEngine.getVisionIconOrDie(this.currentVision);

    // alimentation de la liste des steps
    if (!!this.currentVision.visionSteps) {
      this.stepDS.setSource(Object.values(this.currentVision.visionSteps).map(vs => new VisionStepVO(vs)));
      this.stepDS.clearSelection();
    } else {
      this.stepDS.setSource([]);
    }


    // alimentation de la liste des vsps
    const map: Map<string, VisionStepPerimeter> = Model.getInstance().visionEngine.getStepPerimeterList(this.currentVision);
    const vsps: VisionStepPerimeter[] = map ? Array.from(map.values()) : [];
    let voArr: VisionStepPerimeterVO[] = [];
    vsps.forEach(vsp => {
      voArr.push(new VisionStepPerimeterVO(vsp));
    });
    this.stepPerimetersDS.setSource(voArr);
    this.currentPerimeter = null; // on rince la sélection des périmeters
    this.clonePerimeter = null;


  }

  public doAction(e: WizEvent): void {
    // console.log('doAction', e);
    const steps: number[] = [];
    this.stepDS.selectedItems.forEach((svo: VisionStepVO) => {
      steps.push(svo.level);
    });

    switch (e.component) {
      case this.btnGenerate:
        WizPopUp.confirm('$.visionstep.generate.title.$', '$.visionstep.generate.detail.$',
          this.confirmAction.bind(this), CONFIRMATION_MODE.CONFIRM_CANCEL, [this.generate.bind(this), steps]).show();
        break;
      case this.btnGenerateDelta:
        WizPopUp.confirm('$.visionstep.generate.title.$', '$.visionstep.generate.delta.detail.$',
          this.confirmAction.bind(this), CONFIRMATION_MODE.CONFIRM_CANCEL, [this.generateNewLines.bind(this), steps]).show();
        break;
      case this.btnDelete:
        WizPopUp.confirm('$.visionstep.delete.title.$', '$.visionstep.delete.detail.$',
          this.confirmAction.bind(this), CONFIRMATION_MODE.CONFIRM_CANCEL, [this.deleteLines.bind(this), steps]).show();
        break;
      case this.btnReset:
        WizPopUp.confirm('$.visionstep.reset.title.$', '$.visionstep.reset.detail.$',
          this.confirmAction.bind(this), CONFIRMATION_MODE.CONFIRM_CANCEL, [this.deleteValues.bind(this), steps]).show();
        break;
      case this.btnclearCache:
        this.clearCache();
        break;
    }
  }

  /**
    * callback de la popup de confirmation d'une action sur les lignes de steps
    * @param confirmationResult: objet WizPopupConfirmationResultObj contenant le choix effectué sur la popup
    */
  private confirmAction(confirmationResult: ConfirmationResultObj): void {
    if (confirmationResult.result === CONFIRMATION_RESULT.CONFIRM) {
      const action = confirmationResult.data[0] as Function; // l'action à executer, suite à la confirmation (générer, supprimer les lignes ou supprimer les valeurs) 
      const steps = confirmationResult.data[1] as number[]; // les steps sélectionnés
      action(steps);
    }
  }

  /**
   * génération des lignes de steps
   */
  private generate(steps: number[]): void {
    new VisionManageLinesCmd(Model.getInstance())
      .generate(this.currentVision, steps, this.backFromLineGeneration.bind(this))
      .execute();
  }

  /**
   * génération des lignes complémentaires de steps
   */
  private generateNewLines(steps: number[]): void {
    new VisionManageLinesCmd(Model.getInstance())
      .generateNewLines(this.currentVision, steps, this.backFromLineGeneration.bind(this))
      .execute();
  }

  /**
   * suppression des lignes de step
   */
  private deleteLines(steps: number[]): void {
    this.clearCache();
    new VisionManageLinesCmd(Model.getInstance())
      .deleteLines(this.currentVision, steps, this.backFromLineGeneration.bind(this))
      .execute();
  }

  /**
   * suppression des valeurs de step
   */
  private deleteValues(steps: number[]): void {
    this.clearCache();
    new VisionManageLinesCmd(Model.getInstance())
      .deleteValues(this.currentVision, steps, this.backFromLineGeneration.bind(this))
      .execute();
  }

  /**
   * suppression des Fei en cache local
   */
  private clearCache(): void {
    // pour chaque step, suppression du cache local
    Object.values(this.currentVision.visionSteps).forEach((vs: VisionStep) => {
      const feiKey: string = Model.getInstance().widgetDataService.getFeiLocalKey(vs, ViewData.DEFAULT_SCENARIO); // TODO TODO supprimer tous les DLO pour tous les scenars
      AkmBDD.getInstance().doDelete(AkmBDD.BDD_FEI_KEY, feiKey); // on bute le fei
      // TODO seulement celles de la vision
      AkmBDD.getInstance().clear(AkmBDD.BDD_BASELINE_KEY); // delete des baselines
    });
  }

  private backFromLineGeneration(e: any) {
    // console.log('backFromLine', e);

    if (this.currentVision?.code === Model.getInstance().visionCode) {
      // console.log('backFromLineGeneration', e);
      Model.getInstance().compositeEngine.compositeReload(); // si je modifie la vision en cours, je recharge les données
    }

    new VisionStatsCmd(Model.getInstance()) // chargement des stats
      .do(this.currentVision, this.feed.bind(this))
      .execute();

  }

  public visionLocked(): boolean {
    // TODO gestion des permissions et cycle de vie
    // adminVision : possibilité de modifier les statuts même en cours. Sinon, non
    // => donc rôle AdminVision
    return false;
  }


  /**
   * change sur la zone détail, mise à jour éventuelle de l'updateModel, mise à jour du clone
   * @param e wizevent contenant le composant modifier
   */
  private updateModel(e: WizEvent = null): void {
    if (e && e.component) {
      switch (e.component) {
        case this.ddStep:
          this.clonePerimeter.visionStepLevel = this.stepChooserDS.selectedItem ? this.stepChooserDS.selectedItem.level : null;
          this.feedPivot(); // alimentation du détail des lignes
          break;
        case this.tiCode:
          this.clonePerimeter.code = this.tiCode.wizValue?.toUpperCase();
          break;
        case this.trsLabel:
          this.clonePerimeter.labelVO.updateTranslationEntry(e.value);
          this.clonePerimeter.maintenanceFlag = WizMathUtils.createUUID(); // PATCH MAJ SUBCOLL
          break;
        case this.trsDesc:
          this.clonePerimeter.descriptionVO.updateTranslationEntry(e.value);
          this.clonePerimeter.maintenanceFlag = WizMathUtils.createUUID(); // PATCH MAJ SUBCOLL
          break;
      }
      ViewData.wizUpdateModel = true;
    }
  }


  /* ################################################
                      Batch
    ################################################ */

  public doAct(action: string): void {
    switch (action) {
      case 'export':
        Model.getInstance().batchEngine.export(BATCH_KIND.BASELINE, BATCH_FILE_FORMAT.CSV, Model.getInstance().visionCode);
        break;
      case 'import':
        Model.getInstance().batchEngine.import(BATCH_KIND.BASELINE);
        break;
      case 'deleteVSP':
        this.askForDelete();
        break;
      case 'createVSP':
        this.askForAddVsp();
        break;
    }
  }

  /* ################################################
                     gestion perimeters
    ################################################ */

  @ViewChild('ddStep', { read: WizDropdown }) ddStep: WizDropdown; // drop de choix du step pour les perimeters
  @ViewChild('tiCode', { read: WizTextInput }) tiCode: WizTextInput; // code du perimeter
  @ViewChild('trsLabel', { read: WizTranslationBasic }) trsLabel: WizTranslationBasic; // libellé traduit
  @ViewChild('trsDesc', { read: WizTranslationBasic }) trsDesc: WizTranslationBasic; // desc du perimeter
  @ViewChild('vspKeyMatrix', { read: ToolPivot }) vspKeyMatrix: ToolPivot; // arbre du prototype de step

  // @ViewChild('treeVspPrototype', { read: WizTree }) treeVspPrototype: WizTree<any>; // arbre du prototype de step

  @ViewChild('btnSaveStep', { read: WizButton }) btnSaveStep: WizButton; // btn génération sample
  @ViewChild('btnCancelStep', { read: WizButton }) btnCancelStep: WizButton; // btn génération sample

  public hasPerimeter(): boolean {
    return !!this.currentPerimeter;
  }

  public currentPerimeter: VisionStepPerimeterVO = null; // vsp en cours, sert aussi de flag pour afficher le détail / le cartouche
  public clonePerimeter: VisionStepPerimeterVO = null; // le clone de travail

  public loadedFeiMap: Map<string, Map<number, FeiVO>> = null; // la map des FEIs déjà chargés par visionCode = stepLevel = feiVO
  public sizeMap: Map<number, number> = new Map(); // la map pour la vision en cours, affichage des métriques



  /**
   * clic sur le bouton "ajouter un vsp"
   * @param e void
   */
  private askForAddVsp(): void {
    this.currentPerimeter = Model.getInstance().visionEngine.buildFakeVsp(this.currentVision);
    this.feedVsp();
  }

  /**
   * Callback de selection dans le grid
   * @param pEvent WizEvent
   */
  private selectVsp(e: WizEvent): void {
    const vo: VisionStepPerimeterVO = e.value;
    // this.currentVision = e.value ? e.value : null; 
    if (!this.stepPerimetersDS.isSelected(vo)) {
      // pas encore sélectionné
      this.currentPerimeter = vo;
    } else {
      // déjà sélectionné, déselection
      this.currentPerimeter = null;
    }

    if (!!this.currentPerimeter) {
      // vsp pas encore chargés
      if (!this.currentPerimeter.loaded) { // TODO utiliser la map
        new VisionStepPerimeterCommand(Model.getInstance()).get(this.currentPerimeter, this.feedVsp.bind(this)).execute();
      } else {
        this.feedVsp();
      }
    }
    else {
      this.feedVsp();
    }

    this.cdref.markForCheck();
  }

  public get showGrid(): boolean {
    return this.clonePerimeter?.visionStepLevel > 0;
  }



  private resetVsp(): void {
    this.clonePerimeter = null;
    this.cdref.markForCheck();
  }

  /**
    * Initialisation des valeurs des champs dans le conteneur du périmeter
    */
  public feedVsp(): void {
    if (!this.currentPerimeter) { // uniquement si le perimeter est alimenté
      this.resetVsp();
      return;
    }

    this.clonePerimeter = Model.getInstance().visionEngine.cloneVsp(this.currentPerimeter);

    this.tiCode.wizValue = this.clonePerimeter.code;
    this.trsLabel.translationData = Model.getInstance().localeEngine.buildTranslationData(this.clonePerimeter.labelVO);
    this.trsDesc.translationData = Model.getInstance().localeEngine.buildTranslationData(this.clonePerimeter.descriptionVO);
    let vsvo: VisionStepVO = null;
    this.stepChooserDS.setSource(Object.values(this.currentVision.visionSteps).map(vs => new VisionStepVO(vs)));
    if (this.clonePerimeter.visionStepLevel) {
      let vs = BaselineEngine.getVisionStepByLevel(this.currentVision, this.clonePerimeter.visionStepLevel);
      vsvo = this.stepChooserDS.getItemByKey(vs.code);
      // this.currentVision.getVisionStepByLevel(this.clonePerimeter.visionStepLevel);
    }
    this.stepChooserDS.select([vsvo]);
    this.vspKeyMatrix.context = null;
    this.feedPivot(); // alimentation du détail des lignes du vsp
  }





  /** desactivation des boutons save et cancel sur updateModel */
  public disableVspSave(): boolean {
    let result: boolean = true;
    if (this.clonePerimeter && !!this.clonePerimeter.code && !!this.clonePerimeter.labelVO.hasEntry() && !!this.clonePerimeter.visionStepLevel) {
      result = false;
    }
    if (!result) {
      result = !ViewData.wizUpdateModel;
    }
    return result;
  }

  /** desactivation des boutons save et cancel sur updateModel */
  public disableVspCancel(): boolean {
    return !ViewData.wizUpdateModel;
  }

  /** demande de merge sur btOk */
  private doMerge(e: WizEvent): void {
    // TODO gestion erreur

    // synchronisation des vspKey
    Array.from(this.clonePerimeter.keyMap.keys()).forEach((key: string) => {
      if (this.currentPerimeter.keyMap.has(key)) { // existe déjà, donc mieux
        this.clonePerimeter.keyMap.set(key, this.currentPerimeter.keyMap.get(key));
      }
    });
    this.clonePerimeter.keys = Array.from(this.clonePerimeter.keyMap.values()); // on alimente le clone

    Model.getInstance().visionEngine.mergeVsp(this.clonePerimeter, this.currentPerimeter, this.backFromMerge.bind(this));
  }

  /**
   * Callback de l'ajout ou modification d'une vision
   * @param bddBack vision créée ou modifiée (le clone)
   */
  private backFromMerge(bddBack: VisionStepPerimeterVO): void {
    const vsps: VisionStepPerimeterVO[] = Array.from(Model.getInstance().visionEngine.getStepPerimeterList(this.currentVision).values()).map(item => new VisionStepPerimeterVO(item));
    this.stepPerimetersDS.setSource(vsps);
    // TODO vérifier l'instance, sinon faire un get sur la DS
    this.stepPerimetersDS.select([bddBack]);
    this.currentPerimeter = bddBack;
    this.clonePerimeter = Model.getInstance().visionEngine.cloneVsp(this.currentPerimeter);
    this.feedVsp();
  }

  /**
   * revert sur btCancel, simple resélection
   * @param e wizEvent
   */
  private doRevert(e: WizEvent): void {
    this.clonePerimeter = Model.getInstance().visionEngine.cloneVsp(this.currentPerimeter);
    ViewData.wizUpdateModel = false;
    this.feedVsp();
  }

  /** demande de suppression la vision */
  public askForDelete(): void {
    WizPopUp.confirm(
      '$.common.delete.title.$',
      '$.common.delete.detail.$',
      this.doRemove.bind(this),
      CONFIRMATION_MODE.CONFIRM_CANCEL,
      this.currentPerimeter
    ).show();
  }

  /**
   * Suppression du vsp
   * @param result Objet de retour sur popUp de confirmation. Contient l'action choisie, et la data d'origine
   */
  private doRemove(result: ConfirmationResultObj): void {
    if (result.result === CONFIRMATION_RESULT.CONFIRM) {
      Model.getInstance().visionEngine.removeVsp(this.clonePerimeter, this.currentPerimeter, this.backFromRemove.bind(this));
    }
  }

  /** suppression du vsp
   * @param e: le vsp supprimé
   */
  private backFromRemove(vsp: VisionStepPerimeterVO): void {
    // TODO supprimer de la DS
    this.currentPerimeter = null;
    this.clonePerimeter = null;
    this.stepPerimetersDS.removeItem(vsp);
    this.stepPerimetersDS.clearSelection();
    this.feedVsp();
  }

  /**
   * alimentation du toolPivot des VSPs
   * 
   */
  private feedPivot(): void {

    if (!this.clonePerimeter.visionStepLevel) { // pas encore de stepLevel, on masque la zone
      this.cdref.markForCheck();
      return;
    }

    if (!this.loadedFeiMap?.has(this.currentVision?.code)) {
      this.buildFei(); // construction du paquet de FEI et alimentation de this.loadedFeiMap
      this.vspKeyMatrix.context = null;
    }
    const fei: FeiVO = this.loadedFeiMap.get(this.currentVision.code)?.get(this.clonePerimeter.visionStepLevel);

    this.vspKeyMatrix.state = WIDGET_STATE.READY;
    this.vspKeyMatrix.vision = fei.vision;
    this.vspKeyMatrix.visionStep = fei.visionStep;
    fei.visionStepPerimeterVO = this.clonePerimeter; // affectation du vsp
    this.vspKeyMatrix.fei = fei; // on byPass le setDatasource
    if (!this.vspKeyMatrix.context) {
      this.vspKeyMatrix.context = this.vspKeyMatrix.generateNewContext(this.vspKeyMatrix.widgetKind); // création d'un contexte minimal
    }
    this.vspKeyMatrix.alterContent(WIDGET_ACTION_ORIGIN.READY); // on déclenche le cycle de vie
  }


  /** construction d'un FEI à la demande, et alimentation de la map  */
  private buildFei(): void {

    // construction des baselines pour toute la vision. TODO optimiser
    // console.time('computeSample');
    const fullLinestMap: Map<number, Map<string, Baseline>> = Model.getInstance().incExcSampleEngine.buildFakeBaselines(this.currentVision, Model.getInstance().incExcKeyCollection);
    // console.timeEnd('computeSample');

    Array.from(fullLinestMap.keys()).forEach((lvl: number) => {
      this.sizeMap.set(lvl, fullLinestMap.get(lvl).size);
    });

    if (!this.loadedFeiMap) {
      this.loadedFeiMap = new Map<string, Map<number, FeiVO>>();
    }
    if (!this.loadedFeiMap.get(this.currentVision.code)) {
      this.loadedFeiMap.set(this.currentVision.code, new Map<number, FeiVO>());
    }


    // on alimente tous les steps
    Array.from(fullLinestMap.keys()).forEach((level: number) => {
      const fei: FormulaEngineInstance = new FormulaEngineInstance();
      fei.baselines = {};
      fei.vision = this.currentVision.source;
      fei.visionStep = BaselineEngine.getVisionStepByLevel(this.currentVision, level);

      // on crée une entrée en Fake (scenar @@FAKE)
      let fakeFei: FeiVO = new FeiVO(fei, Model.getInstance(), this.currentVision, fei.visionStep, WizConstants.SCEN_FAKE_KEY_CODE);
      fakeFei.model = Model.getInstance();
      Model.getInstance().feiEngine.buildToolFEIKeys(fakeFei, FeiEngine.TOOL_MODE_VSP, [...fullLinestMap.get(fei.visionStep.level).values()]);
      this.loadedFeiMap.get(this.currentVision.code).set(level, fakeFei);
    });

  }

  /**
   * modification du pool du vsp
   * @param e 
   */
  private updateVspSelect(e: AlkEvent): void {
    ViewData.wizUpdateModel = true;
    this.clonePerimeter.maintenanceFlag = WizMathUtils.createUUID(); // force l'update sur maj subcoll
  }




}
